import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { BirdsnestRangeSlider } from './BirdsnestRangeSlider';
import { BirdsnestColorSwatchGrid } from './BirdsnestColorSwatchGrid';
import { BirdsnestCheckboxList } from './BirdsnestCheckboxList';
import styles from './BirdsnestFilterDrawer.module.scss';
/**
 * Filter drawer refactor: single source of truth from orderedFilterItems
 *
 *
 * All filter state (standalone + inside groups) is now a single ordered list; helpers above
 * (cloneOrderedFilterItems, getAllFiltersFromOrderedItems, resetOrderedFilterItems) operate on
 * that shape so we never split by group then re-merge.
 */
function cloneFilter(filter) {
    return Object.assign(Object.assign({}, filter), { options: filter.options
            ? filter.options.map(option => (Object.assign({}, option)))
            : filter.options });
}
export function cloneOrderedFilterItems(items) {
    return items.map(item => item.type === 'filter'
        ? { type: 'filter', filter: cloneFilter(item.filter) }
        : {
            type: 'group',
            group: Object.assign(Object.assign({}, item.group), { filters: item.group.filters.map(cloneFilter) }),
        });
}
export function getAllFiltersFromOrderedItems(items) {
    return items.flatMap(item => item.type === 'filter' ? [item.filter] : item.group.filters);
}
function resetFilter(filter) {
    var _a, _b;
    return Object.assign(Object.assign({}, filter), { currentMin: (_a = filter.min) !== null && _a !== void 0 ? _a : filter.currentMin, currentMax: (_b = filter.max) !== null && _b !== void 0 ? _b : filter.currentMax, options: filter.options
            ? filter.options.map(opt => (Object.assign(Object.assign({}, opt), { selected: false })))
            : filter.options });
}
export function resetOrderedFilterItems(items) {
    return items.map(item => item.type === 'filter'
        ? { type: 'filter', filter: resetFilter(item.filter) }
        : {
            type: 'group',
            group: Object.assign(Object.assign({}, item.group), { filters: item.group.filters.map(resetFilter) }),
        });
}
// Updates a single filter's counts from the latest API response, preserving all options and selected state.
// `isActive` = the user has actually selected a range for this filter (present in activeFilters).
// A fresh globalrange from Fredhopper (e.g. after selecting an unrelated category, which
// narrows the price range) must reset currentMin/currentMax to match — otherwise they keep
// pointing at the *previous* min/max, which no longer equals the new base range, and the
// "changed from base" check in buildFilterMap mistakes that drift for a real user edit and
// silently adds a price filter nobody selected.
function mergeFilterCounts(local, api, isActive) {
    var _a, _b, _c, _d;
    if (local.type === 'range' || local.type === 'slider') {
        const min = (_a = api.min) !== null && _a !== void 0 ? _a : local.min;
        const max = (_b = api.max) !== null && _b !== void 0 ? _b : local.max;
        if (isActive) {
            return Object.assign(Object.assign({}, local), { min, max });
        }
        return Object.assign(Object.assign({}, local), { min, max, currentMin: min, currentMax: max });
    }
    if (!local.options)
        return local;
    const apiCounts = new Map((_d = (_c = api.options) === null || _c === void 0 ? void 0 : _c.map(o => [o.id, o.count])) !== null && _d !== void 0 ? _d : []);
    return Object.assign(Object.assign({}, local), { options: local.options.map(o => { var _a; return (Object.assign(Object.assign({}, o), { count: (_a = apiCounts.get(o.id)) !== null && _a !== void 0 ? _a : 0 })); }) });
}
// When Fredhopper drops a filter entirely from the response, zero out all option counts
// Any option the user already selected keeps selected:true so it stays visible and deselectable
function withZeroedCounts(filter) {
    if (!filter.options)
        return filter;
    return Object.assign(Object.assign({}, filter), { options: filter.options.map(o => (Object.assign(Object.assign({}, o), { count: 0 }))) });
}
// Merges updated counts from API data into the drawer's local items without removing any options.
export function syncCountsFromApi(localItems, apiItems, activeFilters = {}) {
    const apiById = new Map();
    for (const item of apiItems) {
        if (item.type === 'filter') {
            apiById.set(item.filter.id, item.filter);
        }
        else {
            item.group.filters.forEach(f => apiById.set(f.id, f));
        }
    }
    const isActive = (filterId) => { var _a, _b; return ((_b = (_a = activeFilters[filterId]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; };
    return localItems.map(localItem => {
        if (localItem.type === 'filter') {
            const api = apiById.get(localItem.filter.id);
            return Object.assign(Object.assign({}, localItem), { filter: api
                    ? mergeFilterCounts(localItem.filter, api, isActive(localItem.filter.id))
                    : withZeroedCounts(localItem.filter) });
        }
        return Object.assign(Object.assign({}, localItem), { group: Object.assign(Object.assign({}, localItem.group), { filters: localItem.group.filters.map(f => {
                    const api = apiById.get(f.id);
                    return api ? mergeFilterCounts(f, api, isActive(f.id)) : withZeroedCounts(f);
                }) }) });
    });
}
// Returns only options with products, plus any already-selected ones so the user can deselect them.
export function getVisibleOptions(options) {
    return options.filter(o => o.count > 0 || o.selected);
}
// A filter section is visible if it's a range/slider, or if it has at least one visible option.
export function filterHasVisibleContent(filter) {
    var _a, _b;
    if (filter.type === 'range' || filter.type === 'slider')
        return true;
    return ((_b = (_a = filter.options) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 && getVisibleOptions(filter.options).length > 0;
}
// Returns true if the filter should default to open, based on Fredhopper's "collapsed" custom field.
// collapsed="true" → closed (return false), collapsed="false" or absent → open (return true).
function shouldDefaultOpen(filter) {
    var _a;
    const collapsed = Array.isArray(filter.customAttributes)
        ? (_a = filter.customAttributes.find((attr) => attr.name === 'collapsed')) === null || _a === void 0 ? void 0 : _a.value
        : undefined;
    return collapsed === 'false';
}
function CollapsibleFilter({ filter, onFilterChange, defaultOpen = false, isChildFilter = false }) {
    var _a;
    const [isOpen, setIsOpen] = useState(defaultOpen);
    // Only show options that have products or are currently selected
    const visibleOptions = getVisibleOptions(filter.options || []);
    const renderFilterContent = () => {
        switch (filter.type) {
            case 'range':
            case 'slider':
                return (h(BirdsnestRangeSlider, { min: filter.min || 0, max: filter.max || 1000, gap: filter.gap || 1, currentMin: filter.currentMin || filter.min || 0, currentMax: filter.currentMax || filter.max || 1000, unit: filter.unit || '$', onChange: (values) => {
                        onFilterChange(filter.id, `${values[0]}-${values[1]}`);
                    } }));
            case 'color':
            case 'swatch':
                return (h(BirdsnestColorSwatchGrid, { options: visibleOptions, onChange: (optionId) => onFilterChange(filter.id, optionId) }));
            case 'checkbox':
            case 'text':
            case 'radio':
            default:
                return (h(BirdsnestCheckboxList, { options: visibleOptions, onChange: (optionId) => onFilterChange(filter.id, optionId), multiSelect: filter.multiSelect !== false, isColor: Boolean(filter.id === "custom_colour_group") }));
        }
    };
    return (h("div", { class: `${styles.filterSection} ${isChildFilter ? styles.childFilterSection : ''}` },
        h("button", { class: `${styles.filterHeader} ${isOpen ? styles.filterHeaderOpen : ''}`, onClick: () => setIsOpen(!isOpen), type: "button" },
            h("span", { class: styles.filterLabel }, filter.label.toUpperCase()),
            h("span", { class: `${styles.filterIcon} ${isChildFilter ? styles.childFilterIcon : ''}` }, isOpen ? (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M1 7h12", stroke: "currentColor", strokeWidth: "1.5" }))) : (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M7 1v12M1 7h12", stroke: "currentColor", strokeWidth: "1.5" }))))),
        isOpen && (h("div", { class: `${styles.filterContent} ${((_a = filter.label) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes('colour')) ? styles.colorFilterContent : ''}` }, renderFilterContent()))));
}
function GroupedFilterSection({ groupId, groupLabel, filters, onFilterChange, defaultOpen = false, }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    // Only show child filters that have visible content
    const visibleFilters = filters.filter(filterHasVisibleContent);
    // If no child filters have any visible options, hide the entire group
    if (visibleFilters.length === 0) {
        return null;
    }
    return (h("div", { class: styles.filterSection, id: `grouped-filter-${groupId}` },
        h("button", { class: styles.filterHeader, onClick: () => setIsOpen(!isOpen), type: "button" },
            h("span", { class: styles.filterLabel }, groupLabel.toUpperCase()),
            h("span", { class: styles.filterIcon }, isOpen ? (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M1 7h12", stroke: "currentColor", strokeWidth: "1.5" }))) : (h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none" },
                h("path", { d: "M7 1v12M1 7h12", stroke: "currentColor", strokeWidth: "1.5" }))))),
        isOpen && (h("div", { class: styles.filterContent }, visibleFilters.map(filter => (h(CollapsibleFilter, { key: filter.id, filter: filter, onFilterChange: onFilterChange, defaultOpen: false, isChildFilter: true })))))));
}
export function BirdsnestFilterDrawer({ orderedFilterItems, activeFilters, onApply, onClearAll, isOpen, onClose }) {
    /** Single local state for the whole ordered list (replaces localFilters + localGroupedFilters). */
    const [localOrderedFilterItems, setLocalOrderedFilterItems] = useState([]);
    const [hasInitialized, setHasInitialized] = useState(false);
    // Helper function to merge activeFilters into orderedFilterItems
    const mergeActiveFiltersIntoItems = (items, filters) => {
        return items.map(item => {
            if (item.type === 'filter') {
                const filter = item.filter;
                const activeValues = filters[filter.id];
                if (!activeValues || activeValues.length === 0) {
                    return item;
                }
                // Handle range/slider filters
                if (filter.type === 'range' || filter.type === 'slider') {
                    const rangeValue = activeValues[0];
                    if (rangeValue && rangeValue.includes('-')) {
                        const [minStr, maxStr] = rangeValue.split('-');
                        const currentMin = parseInt(minStr, 10);
                        const currentMax = parseInt(maxStr, 10);
                        return {
                            type: 'filter',
                            filter: Object.assign(Object.assign({}, filter), { currentMin: isNaN(currentMin) ? filter.currentMin : currentMin, currentMax: isNaN(currentMax) ? filter.currentMax : currentMax })
                        };
                    }
                    return item;
                }
                // Handle option-based filters (checkbox, color, etc.)
                if (filter.options) {
                    const updatedOptions = filter.options.map(option => (Object.assign(Object.assign({}, option), { selected: activeValues.includes(option.id) })));
                    return {
                        type: 'filter',
                        filter: Object.assign(Object.assign({}, filter), { options: updatedOptions })
                    };
                }
                return item;
            }
            else {
                // Handle grouped filters
                const updatedFilters = item.group.filters.map(filter => {
                    const activeValues = filters[filter.id];
                    if (!activeValues || activeValues.length === 0) {
                        return filter;
                    }
                    // Handle range/slider
                    if (filter.type === 'range' || filter.type === 'slider') {
                        const rangeValue = activeValues[0];
                        if (rangeValue && rangeValue.includes('-')) {
                            const [minStr, maxStr] = rangeValue.split('-');
                            const currentMin = parseInt(minStr, 10);
                            const currentMax = parseInt(maxStr, 10);
                            return Object.assign(Object.assign({}, filter), { currentMin: isNaN(currentMin) ? filter.currentMin : currentMin, currentMax: isNaN(currentMax) ? filter.currentMax : currentMax });
                        }
                        return filter;
                    }
                    // Handle options
                    if (filter.options) {
                        const updatedOptions = filter.options.map(option => (Object.assign(Object.assign({}, option), { selected: activeValues.includes(option.id) })));
                        return Object.assign(Object.assign({}, filter), { options: updatedOptions });
                    }
                    return filter;
                });
                return {
                    type: 'group',
                    group: Object.assign(Object.assign({}, item.group), { filters: updatedFilters })
                };
            }
        });
    };
    useEffect(() => {
        if (!isOpen) {
            setHasInitialized(false);
            return;
        }
        if (!hasInitialized && orderedFilterItems.length) {
            const itemsWithActiveFilters = mergeActiveFiltersIntoItems(orderedFilterItems, activeFilters);
            setLocalOrderedFilterItems(itemsWithActiveFilters);
            setHasInitialized(true);
        }
    }, [isOpen, orderedFilterItems, hasInitialized, activeFilters]);
    useEffect(() => {
        const drawerIsOpenAndReady = hasInitialized && isOpen && orderedFilterItems.length > 0;
        if (drawerIsOpenAndReady) {
            setLocalOrderedFilterItems(currentLocalItems => syncCountsFromApi(currentLocalItems, orderedFilterItems, activeFilters));
        }
    }, [orderedFilterItems]);
    const applyFilterChange = (filter, filterId, value) => {
        if (filter.id !== filterId)
            return filter;
        if (filter.type === 'range' || filter.type === 'slider') {
            const [minStr, maxStr] = value.split('-');
            const currentMin = parseInt(minStr, 10);
            const currentMax = parseInt(maxStr, 10);
            return Object.assign(Object.assign({}, filter), { currentMin: isNaN(currentMin) ? filter.currentMin : currentMin, currentMax: isNaN(currentMax) ? filter.currentMax : currentMax });
        }
        const multiSelect = filter.multiSelect !== false;
        const options = (filter.options || []).map(option => {
            if (option.id !== value) {
                return multiSelect ? option : Object.assign(Object.assign({}, option), { selected: false });
            }
            if (multiSelect) {
                return Object.assign(Object.assign({}, option), { selected: !option.selected });
            }
            return Object.assign(Object.assign({}, option), { selected: true });
        });
        return Object.assign(Object.assign({}, filter), { options });
    };
    const handleLocalFilterChange = (filterId, value) => {
        const updatedItems = localOrderedFilterItems.map(item => {
            if (item.type === 'filter') {
                return { type: 'filter', filter: applyFilterChange(item.filter, filterId, value) };
            }
            return {
                type: 'group',
                group: Object.assign(Object.assign({}, item.group), { filters: item.group.filters.map(f => applyFilterChange(f, filterId, value)) }),
            };
        });
        setLocalOrderedFilterItems(updatedItems);
        // Auto-apply filters on change
        const allFilters = getAllFiltersFromOrderedItems(updatedItems);
        const newFiltersFromDrawer = buildFilterMap(allFilters);
        const allFilterIdsInDrawer = new Set(allFilters.map(f => f.id));
        const preservedFilters = {};
        Object.entries(activeFilters).forEach(([filterId, values]) => {
            if (!allFilterIdsInDrawer.has(filterId)) {
                preservedFilters[filterId] = values;
            }
        });
        onApply(Object.assign(Object.assign({}, preservedFilters), newFiltersFromDrawer));
    };
    const buildFilterMap = (filtersToMap) => {
        const result = {};
        filtersToMap.forEach(filter => {
            if (filter.type === 'range' || filter.type === 'slider') {
                const baseMin = filter.min;
                const baseMax = filter.max;
                const currentMin = filter.currentMin;
                const currentMax = filter.currentMax;
                if (typeof currentMin === 'number' &&
                    typeof currentMax === 'number' &&
                    (baseMin === undefined ||
                        baseMax === undefined ||
                        currentMin !== baseMin ||
                        currentMax !== baseMax)) {
                    result[filter.id] = [`${currentMin}-${currentMax}`];
                }
            }
            else if (filter.options && filter.options.length > 0) {
                const selectedValues = filter.options
                    .filter(option => option.selected)
                    .map(option => option.id);
                if (selectedValues.length > 0) {
                    result[filter.id] = selectedValues;
                }
            }
        });
        return result;
    };
    /** Render from local state when available, otherwise prop (e.g. before first open). */
    const displayItems = localOrderedFilterItems.length ? localOrderedFilterItems : orderedFilterItems;
    return (h(Fragment, null,
        isOpen && h("div", { class: styles.overlay, onClick: onClose }),
        h("div", { class: `${styles.drawer} ${isOpen ? styles.drawerOpen : ''}` },
            h("div", { class: styles.drawerHeader },
                h("h2", { class: styles.drawerTitle }, "Filter"),
                h("button", { class: styles.closeBtn, onClick: onClose, type: "button", "aria-label": "Close filters" },
                    h("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" },
                        h("path", { d: "M1 1l14 14M1 15L15 1", stroke: "currentColor", strokeWidth: "1.5" })))),
            h("div", { class: styles.divider }),
            h("div", { class: styles.filterSections }, displayItems.map((item, index) => {
                // Hide standalone filters that have no visible options
                if (item.type === 'filter' && !filterHasVisibleContent(item.filter)) {
                    return null;
                }
                return item.type === 'filter' ? (h(CollapsibleFilter, { key: item.filter.id, filter: item.filter, onFilterChange: handleLocalFilterChange, defaultOpen: shouldDefaultOpen(item.filter) })) : (h(GroupedFilterSection, { key: item.group.groupId, groupId: item.group.groupId, groupLabel: item.group.groupLabel, filters: item.group.filters, onFilterChange: handleLocalFilterChange, defaultOpen: item.group.filters.length > 0 && item.group.filters.every(shouldDefaultOpen) }));
            })),
            h("div", { class: styles.drawerFooter },
                h("button", { class: styles.clearBtn, onClick: () => {
                        setLocalOrderedFilterItems(prev => resetOrderedFilterItems(prev));
                        onClearAll();
                        onClose();
                    }, type: "button" }, "CLEAR ALL"),
                h("button", { class: styles.applyBtn, onClick: () => {
                        const allFilters = getAllFiltersFromOrderedItems(localOrderedFilterItems);
                        const newFiltersFromDrawer = buildFilterMap(allFilters);
                        const allFilterIdsInDrawer = new Set(allFilters.map(f => f.id));
                        const preservedFilters = {};
                        Object.entries(activeFilters).forEach(([filterId, values]) => {
                            if (!allFilterIdsInDrawer.has(filterId)) {
                                preservedFilters[filterId] = values;
                            }
                        });
                        onApply(Object.assign(Object.assign({}, preservedFilters), newFiltersFromDrawer));
                        onClose();
                    }, type: "button" }, "APPLY")))));
}
